import { useCallback, useEffect, useRef, useState } from 'react';
import { gql } from '@apollo/client';
import { useSubscription } from '@apollo/client/react';

/** Backoff exponentiel entre deux tentatives de re-souscription au flux multipart. */
const RESUBSCRIBE_BASE_DELAY_MS = 1000;
const RESUBSCRIBE_MAX_DELAY_MS = 30000;

/** Types d'evenements emis par `ChatMessagePublisher` cote backend. */
export const CHAT_MESSAGE_EVENT_TYPES = {
    NEW: 'NEW_CHAT_MESSAGE',
    UPDATE: 'UPDATE_CHAT_MESSAGE',
    DELETE: 'DELETE_MESSAGE',
};

const ONBOARDING_CHAT_MESSAGE_EVENT = gql`
    subscription OnboardingChatMessageEvent {
        onboardingChatMessageEvent {
            eventType
            message {
                uuid
                candidateUuid
                senderName
                adminSender
                content
                modified
                createdAt
                adminReadAt
                candidateReadAt
            }
        }
    }
`;

/**
 * Souscrit au flux temps reel du chat onboarding, transporte en HTTP `multipart/mixed`
 * par le `HttpLink` unique (cf. multipartHttpLink dans apolloClient.js) : pas de WebSocket,
 * donc pas de second canal a authentifier.
 *
 * @param {Object} options
 * @param {(event: {eventType: string, message: Object}) => void} options.onEvent appele a chaque evenement recu
 * @param {() => void} [options.onResync] appele apres chaque re-souscription, pour combler les evenements perdus
 * @param {boolean} [options.skip] desactive la souscription
 */
const useOnboardingChatMessageEvent = ({ onEvent, onResync, skip = false }) => {
    // Apollo ne relance pas de lui-meme un flux multipart termine ou coupe : on force
    // le demontage puis le remontage de la souscription via `isStreamPaused`/`attempt`.
    const [attempt, setAttempt] = useState(0);
    const [isStreamPaused, setIsStreamPaused] = useState(false);
    const retryCountRef = useRef(0);
    const retryTimerRef = useRef(null);
    const onEventRef = useRef(onEvent);
    const onResyncRef = useRef(onResync);
    const skipRef = useRef(skip);

    useEffect(() => {
        onEventRef.current = onEvent;
    }, [onEvent]);

    useEffect(() => {
        onResyncRef.current = onResync;
    }, [onResync]);

    useEffect(() => {
        skipRef.current = skip;
    }, [skip]);

    const scheduleResubscribe = useCallback(() => {
        // Une reconnexion deja planifiee : ne pas empiler les timers ni reinitialiser le backoff.
        if (retryTimerRef.current || skipRef.current) return;

        setIsStreamPaused(true);

        const delay = Math.min(
            RESUBSCRIBE_BASE_DELAY_MS * 2 ** retryCountRef.current,
            RESUBSCRIBE_MAX_DELAY_MS
        );
        retryCountRef.current += 1;

        retryTimerRef.current = setTimeout(() => {
            retryTimerRef.current = null;
            setAttempt((value) => value + 1);
            setIsStreamPaused(false);
        }, delay);
    }, []);

    const { error } = useSubscription(ONBOARDING_CHAT_MESSAGE_EVENT, {
        skip: skip || isStreamPaused,
        onData: ({ data }) => {
            // Le flux repond : le backoff repart de zero pour la prochaine coupure.
            retryCountRef.current = 0;

            const event = data?.data?.onboardingChatMessageEvent;
            if (!event?.message) return;

            onEventRef.current?.(event);
        },
        onError: scheduleResubscribe,
        // Le serveur a ferme la reponse multipart : sans relance, plus aucun message n'arrive.
        onComplete: scheduleResubscribe,
    });

    // Les evenements emis pendant la coupure ne sont jamais rejoues : a chaque
    // re-souscription on recharge l'historique pour combler le trou.
    useEffect(() => {
        if (attempt === 0 || isStreamPaused) return;
        onResyncRef.current?.();
    }, [attempt, isStreamPaused]);

    // Retour du reseau : inutile d'attendre la fin du backoff pour retenter.
    useEffect(() => {
        const handleOnline = () => {
            retryCountRef.current = 0;
            scheduleResubscribe();
        };

        window.addEventListener('online', handleOnline);
        return () => {
            window.removeEventListener('online', handleOnline);
        };
    }, [scheduleResubscribe]);

    useEffect(() => {
        return () => {
            if (retryTimerRef.current) {
                clearTimeout(retryTimerRef.current);
                retryTimerRef.current = null;
            }
        };
    }, []);

    return { error, isStreamInterrupted: isStreamPaused };
};

export default useOnboardingChatMessageEvent;
